import React, { useState, useEffect } from 'react';
import { ShoppingCart, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { addToCart, isInCart, CART_UPDATED_EVENT } from '@/lib/cartStore';
import { fireEvent } from '@/lib/analytics';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface AddToCartButtonProps {
  domain: string;
  tld: string;
  binPrice: number;
  location?: 'list' | 'detail';
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
  disabled?: boolean;
  isUnavailable?: boolean; // P20250925-108: Block unavailable domains
}

export default function AddToCartButton({
  domain,
  tld,
  binPrice,
  location = 'list',
  variant = 'default',
  size = 'default',
  className,
  disabled = false,
  isUnavailable = false, // P20250925-108: Block unavailable domains
}: AddToCartButtonProps) {
  const [inCart, setInCart] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Check if item is in cart on mount and when cart updates
  useEffect(() => {
    setInCart(isInCart(domain));
  }, [domain]);

  useEffect(() => {
    const handleCartUpdate = () => {
      setInCart(isInCart(domain));
    };

    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    };
  }, [domain]);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // P20250925-108: Block if unavailable
    if (inCart || isAdding || disabled || isUnavailable) return;

    setIsAdding(true);

    try {
      const added = addToCart({
        domain,
        tld,
        binPrice,
      });

      if (added) {
        setInCart(true);

        // Track analytics
        fireEvent('add_to_cart', {
          domain_id: domain,
          slug: domain,
          price: binPrice,
          location,
        });

        toast({
          title: 'Added to cart',
          description: `${domain} has been added to your cart.`,
        });
      } else {
        toast({
          title: 'Already in cart',
          description: `${domain} is already in your cart.`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: 'Error',
        description: 'Failed to add item to cart. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Button
      variant={inCart ? 'outline' : variant}
      size={size}
      onClick={handleAddToCart}
      disabled={disabled || isAdding || inCart || isUnavailable}
      className={cn(
        'transition-all duration-200',
        inCart && 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
        isUnavailable && 'opacity-50 cursor-not-allowed',
        className,
      )}
      aria-label={
        isUnavailable
          ? `${domain} is unavailable`
          : inCart
            ? `${domain} is in cart`
            : `Add ${domain} to cart`
      }
      aria-pressed={inCart}
      title={isUnavailable ? 'This domain is not available' : undefined}
    >
      {isAdding ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
          Adding...
        </>
      ) : isUnavailable ? (
        <>Unavailable</>
      ) : inCart ? (
        <>
          <Check className="w-4 h-4 mr-2" />
          In Cart
        </>
      ) : (
        <>
          <ShoppingCart className="w-4 h-4 mr-2" />
          Add to Cart
        </>
      )}
    </Button>
  );
}
